import { useState, useEffect } from 'react';
import axios from 'axios';
import { fmt } from '../../utils/format';
import useLang from '../../hooks/useLang';
import { t, getLang } from '../../utils/i18n';

const api = axios.create({ baseURL: '/api', timeout: 60000 });

export default function AIInsight({ symbol }) {
  const { lang } = useLang();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    setInsight(null);
    setError(null);
    api.get(`/ai/insight/${symbol}`)
      .then(r => { if (r.data) setInsight(r.data); })
      .catch(() => {});
  }, [symbol]);

  const generate = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post('/ai/insight', { symbol, force, lang: getLang() });
      setInsight(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate AI insight. Check your API key.');
    } finally { setLoading(false); }
  };

  const verdictColor = insight?.verdict === 'BUY' ? 'var(--green)'
    : insight?.verdict === 'SELL' ? 'var(--red)' : 'var(--yellow)';

  const meta = insight?._meta;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div className="card-title" style={{ margin: 0 }}>{t('aiInvestmentThesis', lang)}</div>
          {meta && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {t('generatedAgo', lang)} {meta.ageHours}h · {t('priceAtGeneration', lang)}: {fmt.currency(meta.priceAtGeneration)}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {insight && (
            <button className="btn btn-ghost" onClick={() => generate(true)} disabled={loading} style={{ fontSize: 12 }}>
              {t('refresh', lang)}
            </button>
          )}
          {!insight && !loading && (
            <button className="btn btn-primary" onClick={() => generate(false)} disabled={loading}>
              {t('generateInsight', lang)}
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {!insight && !loading && !error && (
        <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px 0', fontSize: 14 }}>
          {t('noSavedInsight', lang)} {symbol}. {t('clickToGenerate', lang)}
        </div>
      )}

      {loading && (
        <div style={{ padding: '40px 0' }}>
          <div className="skeleton" style={{ height: 16, width: '80%', margin: '8px auto' }} />
          <div className="skeleton" style={{ height: 16, width: '60%', margin: '8px auto' }} />
          <div className="skeleton" style={{ height: 16, width: '70%', margin: '8px auto' }} />
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 16, fontSize: 12 }}>{t('analysingWith', lang)}</div>
        </div>
      )}

      {insight && !loading && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            <div className="stat-cell" style={{ flex: 1 }}>
              <div className="stat-label">{t('verdictLabel', lang)}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: verdictColor }}>{insight.verdict}</div>
            </div>
            <div className="stat-cell" style={{ flex: 2 }}>
              <div className="stat-label">{t('priceTargetRange', lang)}</div>
              <div className="stat-value" style={{ fontSize: 18 }}>
                {fmt.currency(insight.priceTarget?.low)} – {fmt.currency(insight.priceTarget?.high)}
              </div>
              {meta?.priceAtGeneration && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {t('upsideToHigh', lang)}: {(((insight.priceTarget?.high - meta.priceAtGeneration) / meta.priceAtGeneration) * 100).toFixed(1)}%
                </div>
              )}
            </div>
            <div className="stat-cell" style={{ flex: 1 }}>
              <div className="stat-label">{t('timeHorizon', lang)}</div>
              <div className="stat-value">{insight.timeHorizon}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={{ background: 'var(--green-dim)', border: '1px solid var(--green)', borderRadius: 6, padding: 14 }}>
              <div style={{ fontWeight: 600, color: 'var(--green)', marginBottom: 10, fontSize: 13 }}>{t('bullCase', lang)}</div>
              {(insight.bullCase || []).map((point, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--green)', flexShrink: 0 }}>+</span>
                  <span>{point}</span>
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red)', borderRadius: 6, padding: 14 }}>
              <div style={{ fontWeight: 600, color: 'var(--red)', marginBottom: 10, fontSize: 13 }}>{t('bearCase', lang)}</div>
              {(insight.bearCase || []).map((point, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--red)', flexShrink: 0 }}>–</span>
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>

          {insight.primaryRisk && (
            <div style={{ background: 'var(--yellow-dim)', border: '1px solid var(--yellow)', borderRadius: 6, padding: '10px 14px', fontSize: 13 }}>
              <span style={{ color: 'var(--yellow)', fontWeight: 600 }}>{t('primaryRisk', lang)} </span>
              <span style={{ color: 'var(--text-secondary)' }}>{insight.primaryRisk}</span>
            </div>
          )}

          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button className="btn btn-ghost" onClick={() => generate(true)} disabled={loading} style={{ fontSize: 12 }}>
              {t('regenerate', lang)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
